/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { TranslocoService } from '@ngneat/transloco';
import * as moment from 'moment';
import { Observable, defer, firstValueFrom, forkJoin, from as fromPromise } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import {
  Availability,
  BookingDetails,
  BookingFormDetails,
  BookingFormUpsertRequest,
  BookingHost,
  WeeklyWindow,
} from '../../models/bookings/v3-booking.model';
import {
  AvailableSlot,
  BookableAppointment,
  BookableAppointmentRequest,
  BookableResource,
  BookableResourceRequest,
  BookableSchedule,
  BookableSlot,
  BookableSlotAdmin,
  BookableSlotAdminRequest,
  BookedAppointment,
  BookedAppointmentsRequest,
  BookingRequest,
  BulkCancelBookingsRequest,
  ScheduleAvailabilityRequest,
  ScheduleRequest,
  ScheduleWithSlots,
} from '../../models/editor/booking-content.model';
import { ObservableResult, Result, SuccessResult } from '../../store';
import { V3BookingDataService } from '../bookings/v3-booking-data.service';
import { BookingDataService } from './booking-data.service';

const RESOURCE_SEPARATOR = '|';
const SLOT_ID_SEPARATOR = '|';
const DEFAULT_BOOKING_WINDOW_DAYS = 28;
const DEFAULT_CANCELLATION_HOURS = 24;
const MY_BOOKINGS_PAGE_SIZE = 100;

const encodeResourceId = (formUid: string, hostUserUid: string): string => `${formUid}${RESOURCE_SEPARATOR}${hostUserUid}`;

const decodeResourceId = (resourceId: string): { formUid: string; hostUserUid: string } | null => {
  const separatorIndex = (resourceId ?? '').indexOf(RESOURCE_SEPARATOR);
  if (separatorIndex < 0) return null;
  return { formUid: resourceId.slice(0, separatorIndex), hostUserUid: resourceId.slice(separatorIndex + 1) };
};

const toTime = (value?: string | null): string => (value ?? '').slice(0, 5);

const toSlotId = (window: WeeklyWindow): string =>
  [window.weekday, toTime(window.startTime), toTime(window.endTime)].join(SLOT_ID_SEPARATOR);

const toWeeklyWindow = (request: { weekday: string; startTime: string; endTime?: string | null }): WeeklyWindow => ({
  weekday: request.weekday.toUpperCase(),
  startTime: toTime(request.startTime),
  endTime: toTime(request.endTime),
});

const toSlotAdmin = (window: WeeklyWindow): BookableSlotAdmin => ({
  id: toSlotId(window),
  weekday: window.weekday,
  startTime: toTime(window.startTime),
  endTime: toTime(window.endTime),
});

const toAppointment = (form: BookingFormDetails): BookableAppointment => ({
  id: form.uid,
  organizationUid: '',
  apiServiceUid: '',
  name: form.title,
  duration: moment.duration(form.sessionDuration),
  bookableInterval: moment.duration(form.sessionDuration),
  bufferTimeBefore: moment.duration(0),
  bufferTimeAfter: moment.duration(0),
  bookingWindowStart: moment.duration(0),
  bookingWindowEnd: moment.duration(DEFAULT_BOOKING_WINDOW_DAYS, 'days'),
  cancellationThreshold: moment.duration(DEFAULT_CANCELLATION_HOURS, 'hours'),
  enabled: form.status === 'ACTIVE',
});

const toResource = (form: BookingFormDetails, host: BookingHost): BookableResource => {
  const resourceId = encodeResourceId(form.uid, host.userUid);
  return {
    id: resourceId,
    bookableAppointment: { id: form.uid, name: form.title },
    organizationUid: '',
    targetResourceSummary: {
      uid: host.userUid,
      name: host.name ?? '',
      email: '',
      imageUrl: null,
      bookableResourceUid: resourceId,
    },
  };
};

const toSchedule = (form: BookingFormDetails, host: BookingHost): BookableSchedule => ({
  id: encodeResourceId(form.uid, host.userUid),
  bookableResource: toResource(form, host),
  startsAt: new Date(),
  endsAt: null,
  createdAt: new Date(),
  lastUpdatedAt: null,
});

const toBookedAppointment = (booking: BookingDetails): BookedAppointment => ({
  id: booking.uid,
  bookableResource: {
    id: encodeResourceId(booking.bookingFormUid, booking.hostUserUid),
    bookableAppointment: { id: booking.bookingFormUid, name: booking.formTitle },
    organizationUid: '',
    targetResourceSummary: {
      uid: booking.hostUserUid,
      name: booking.hostName ?? '',
      email: '',
      imageUrl: null,
    },
  },
  userUid: booking.learnerUserUid,
  createdAt: new Date(booking.createdAt),
  lastUpdatedAt: null,
  isCanceled: booking.status === 'CANCELLED' || booking.status === 'DECLINED',
  startsAt: new Date(booking.startDateTime),
  endsAt: new Date(booking.endDateTime),
  canceledAt: null,
  bookedByUser: { uid: booking.learnerUserUid, name: booking.learnerName },
  card: booking.cardUid
    ? { uri: booking.cardUri, cardUid: booking.cardUid, formTitle: booking.formTitle, cardTitle: booking.formTitle }
    : null,
});

const toUpsertRequest = (form: BookingFormDetails): BookingFormUpsertRequest => ({
  title: form.title,
  hostAssignment: form.hostAssignment,
  sharingScope: form.sharingScope,
  sharedWithUserUids: form.sharedWithUserUids,
  cardUid: form.cardUid,
  cardUri: form.cardUri,
  hosts: (form.hosts ?? []).filter((host) => !host.viaGroupUid),
  hostGroupUids: (form.hostGroups ?? []).map((group) => group.uid),
  sessionDuration: form.sessionDuration,
  slotInterval: form.sessionDuration,
  availability: form.availability,
  timeZone: form.timeZone,
  meetingType: form.meetingType,
  location: form.location,
  meetingUrl: form.meetingUrl,
  instructions: form.instructions,
  requiresApproval: form.requiresApproval,
  notifyLearner: form.notifyLearner,
  notifyHost: form.notifyHost,
  addToHostCalendar: form.addToHostCalendar,
});

const withWeeklyWindows = (availability: Availability, weeklyWindows: WeeklyWindow[]): Availability => ({
  ...availability,
  kind: 'WEEKLY_RECURRING',
  weeklyWindows,
});

const mapResult = <T, U>(source: ObservableResult<T>, mapper: (value: T) => U): ObservableResult<U> =>
  source.pipe(map((result) => (result.isSuccess ? new SuccessResult(mapper(result.value as T)) : result))) as ObservableResult<U>;

export class BookingFormsBookingDataService implements BookingDataService {
  private formCache = new Map<string, Observable<BookingFormDetails | null>>();
  private pendingWrites = new Map<string, Promise<unknown>>();

  constructor(
    private bookingForms: V3BookingDataService,
    private translocoService: TranslocoService,
  ) {}

  createBookableAppointment(request: BookableAppointmentRequest, cardUid: string): ObservableResult<BookableAppointment> {
    const upsertRequest: BookingFormUpsertRequest = {
      title: request.name,
      hostAssignment: 'NAMED_HOST',
      sharingScope: 'PRIVATE',
      cardUid,
      hosts: [],
      sessionDuration: moment.duration(request.duration).toISOString(),
      slotInterval: moment.duration(request.bookableInterval).toISOString(),
      availability: { kind: 'WEEKLY_RECURRING', weeklyWindows: [], dateWindows: [], blockedPeriods: [] },
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      meetingType: 'ONLINE',
      requiresApproval: false,
      notifyLearner: true,
      notifyHost: true,
      addToHostCalendar: false,
    };
    return mapResult(this.bookingForms.createForm(upsertRequest), toAppointment);
  }

  getBookableAppointment(bookableAppointmentUid: string, languageCode?: string): ObservableResult<BookableAppointment> {
    return this.routeByForm(bookableAppointmentUid, (form) => ObservableResult.ofSuccess(toAppointment(form)));
  }

  updateBookableAppointment(
    appointmentUid: string,
    request: BookableAppointmentRequest,
    cardUid: string,
    languageCode: string,
  ): ObservableResult<BookableAppointment> {
    return this.routeByForm(appointmentUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({ ...current, title: request.name })),
        toAppointment,
      ),
    );
  }

  createBookableResource(request: BookableResourceRequest, cardUid: string, languageCode?: string): ObservableResult<BookableResource> {
    return this.routeByForm(request.bookableAppointmentUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({ ...current, hosts: [...current.hosts, { userUid: request.userUid }] })),
        (updated) => toResource(updated, updated.hosts.find((host) => host.userUid === request.userUid) ?? { userUid: request.userUid }),
      ),
    );
  }

  updateBookableResource(
    request: BookableResourceRequest,
    bookableResourceUid: string,
    cardUid: string,
    languageCode?: string,
  ): ObservableResult<BookableResource> {
    return this.withHost(bookableResourceUid, (form, host) => ObservableResult.ofSuccess(toResource(form, host)));
  }

  getAppointmentResources(bookableAppointmentUid: string, cardUid: string, languageCode?: string): ObservableResult<BookableResource[]> {
    return this.routeByForm(bookableAppointmentUid, (form) =>
      ObservableResult.ofSuccess((form.hostPeople ?? form.hosts).map((host) => toResource(form, host))),
    );
  }

  disassociate(cardUid: string, appointmentUid: string, resourceUid: string): ObservableResult<void> {
    return this.routeByForm(appointmentUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({
          ...current,
          hosts: current.hosts.filter((host) => encodeResourceId(form.uid, host.userUid) !== resourceUid),
        })),
        () => undefined,
      ),
    );
  }

  createSchedule(
    request: ScheduleRequest,
    bookableResourceUid: string,
    cardUid: string,
    languageCode?: string,
  ): ObservableResult<BookableSchedule> {
    return this.withHost(bookableResourceUid, (form, host) => ObservableResult.ofSuccess(toSchedule(form, host)));
  }

  updateSchedule(
    request: ScheduleRequest,
    bookableResourceUid: string,
    cardUid: string,
    bookableScheduleUid: string,
    languageCode?: string,
  ): ObservableResult<BookableSchedule> {
    return this.withHost(bookableResourceUid, (form, host) => ObservableResult.ofSuccess(toSchedule(form, host)));
  }

  getSchedule(cardUid: string, resourceUid: string, languageCode?: string): ObservableResult<ScheduleWithSlots> {
    return this.withHost(resourceUid, (form, host) =>
      ObservableResult.ofSuccess({
        schedule: toSchedule(form, host),
        slots: form.availability.weeklyWindows.map(toSlotAdmin),
      }),
    );
  }

  setScheduleAvailability(
    request: ScheduleAvailabilityRequest,
    bookableScheduleUid: string,
    cardUid: string,
    languageCode?: string,
  ): ObservableResult<BookableSlotAdmin> {
    const window = toWeeklyWindow(request);
    return this.withHost(bookableScheduleUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({
          ...current,
          availability: withWeeklyWindows(current.availability, [...current.availability.weeklyWindows, window]),
        })),
        () => toSlotAdmin(window),
      ),
    );
  }

  updateScheduleAvailability(
    request: ScheduleAvailabilityRequest,
    bookableResourceUid: string,
    bookableScheduleUid: string,
    cardUid: string,
    apiScheduleBlockUid: string,
    languageCode?: string,
  ): ObservableResult<BookableSlotAdmin> {
    const window = toWeeklyWindow(request);
    return this.withHost(bookableResourceUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({
          ...current,
          availability: withWeeklyWindows(
            current.availability,
            current.availability.weeklyWindows.map((existing) => (toSlotId(existing) === apiScheduleBlockUid ? window : existing)),
          ),
        })),
        () => toSlotAdmin(window),
      ),
    );
  }

  deleteScheduleAvailability(
    bookableResourceUid: string,
    bookableScheduleUid: string,
    cardUid: string,
    apiScheduleBlockUid: string,
  ): ObservableResult<void> {
    return this.withHost(bookableResourceUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({
          ...current,
          availability: withWeeklyWindows(
            current.availability,
            current.availability.weeklyWindows.filter((existing) => toSlotId(existing) !== apiScheduleBlockUid),
          ),
        })),
        () => undefined,
      ),
    );
  }

  updateResourceSlots(
    cardUid: string,
    bookableResourceUid: string,
    bookableScheduleUid: string,
    request: BookableSlotAdminRequest[],
    languageCode: string,
  ): ObservableResult<BookableSlotAdmin[]> {
    const windows = request.map(toWeeklyWindow);
    return this.withHost(bookableResourceUid, (form) =>
      mapResult(
        this.updateForm(form, (current) => ({ ...current, availability: withWeeklyWindows(current.availability, windows) })),
        () => windows.map(toSlotAdmin),
      ),
    );
  }

  getBookableSlots(bookableAppointmentId: string, from: string, to: string, languageCode?: string): ObservableResult<AvailableSlot[]> {
    return this.routeByForm(bookableAppointmentId, (form) =>
      mapResult(
        this.bookingForms.getSlots(form.uid, new Date(`${from}T00:00:00`).toISOString(), new Date(`${to}T23:59:59`).toISOString()),
        (slots) => {
          const hostsByUid = new Map((form.hosts ?? []).map((host) => [host.userUid, host]));
          const slotsByHost = new Map<string, BookableSlot[]>();
          slots.forEach((slot) => {
            const hostSlots = slotsByHost.get(slot.hostUserUid) ?? [];
            hostSlots.push(new BookableSlot(slot.startDateTime, slot.startDateTime, slot.endDateTime, slot.endDateTime));
            slotsByHost.set(slot.hostUserUid, hostSlots);
          });
          return Array.from(slotsByHost.entries()).map(([hostUserUid, hostSlots]) => ({
            resource: toResource(form, hostsByUid.get(hostUserUid) ?? { userUid: hostUserUid }).targetResourceSummary,
            slots: hostSlots,
          }));
        },
      ),
    );
  }

  placeBooking(bookableResourceUid: string, request: BookingRequest, languageCode: string): ObservableResult<BookedAppointment> {
    const resource = decodeResourceId(bookableResourceUid);
    if (!resource) {
      return this.fail('getResources');
    }
    const toIso = (dateTime: string) => moment.parseZone(`${dateTime} ${request.timeZone}`, 'YYYY-MM-DD HH:mm Z').format();
    return this.findForm(resource.formUid).pipe(
      switchMap((form) =>
        mapResult(
          this.bookingForms.placeBooking(resource.formUid, {
            hostUserUid: resource.hostUserUid,
            cardUid: form?.cardUid,
            startDateTime: toIso(request.start),
            endDateTime: toIso(request.end),
          }),
          toBookedAppointment,
        ),
      ),
    ) as ObservableResult<BookedAppointment>;
  }

  getBooking(bookingUid: string): ObservableResult<BookedAppointment> {
    return mapResult(this.bookingForms.getBooking(bookingUid), toBookedAppointment);
  }

  cancelBooking(bookingUid: string): ObservableResult<BookedAppointment> {
    return mapResult(this.bookingForms.cancelBooking(bookingUid), toBookedAppointment);
  }

  getBookingICalEvent(bookingUid: string): Observable<string> {
    return this.bookingForms.getBookingIcs(bookingUid);
  }

  cancelBulkBookings(request: BulkCancelBookingsRequest): ObservableResult<boolean> {
    if (!request.bookedAppointmentsToCancel.length) {
      return ObservableResult.ofSuccess(true);
    }
    return forkJoin(request.bookedAppointmentsToCancel.map((bookingUid) => this.cancelBooking(bookingUid))).pipe(
      map((results) => new SuccessResult(results.every((result) => result.isSuccess))),
    ) as ObservableResult<boolean>;
  }

  isBookingsDataAvailable(): ObservableResult<boolean> {
    const request: BookedAppointmentsRequest = {
      dateTimeFrom: moment().format('YYYY-MM-DD HH:mm'),
      dateTimeTo: moment().add(1, 'month').format('YYYY-MM-DD HH:mm'),
    };
    return forkJoin([this.getBookings(request), this.getBookedAppointments(request)]).pipe(
      map(([bookings, hosted]) => new SuccessResult((bookings.value?.length ?? 0) + (hosted.value?.length ?? 0) > 0)),
    ) as ObservableResult<boolean>;
  }

  getBookings(request: BookedAppointmentsRequest): ObservableResult<BookedAppointment[]> {
    return mapResult(
      this.bookingForms.getMyBookings(this.toIso(request.dateTimeFrom), this.toIso(request.dateTimeTo), { size: MY_BOOKINGS_PAGE_SIZE }),
      (page) => page.content.map(toBookedAppointment),
    );
  }

  getBookedAppointments(request: BookedAppointmentsRequest): ObservableResult<BookedAppointment[]> {
    return mapResult(
      this.bookingForms.getHostedBookings(this.toIso(request.dateTimeFrom), this.toIso(request.dateTimeTo), {
        size: MY_BOOKINGS_PAGE_SIZE,
      }),
      (page) => page.content.map(toBookedAppointment),
    );
  }

  private toIso(dateTime: string): string {
    return new Date(dateTime.replace(' ', 'T')).toISOString();
  }

  private findForm(formUid: string): Observable<BookingFormDetails | null> {
    const cached = this.formCache.get(formUid);
    if (cached) {
      return cached;
    }
    const request = this.bookingForms.getFormsByUids([formUid]).pipe(
      tap((result) => {
        if (!result.isSuccess) {
          this.formCache.delete(formUid);
        }
      }),
      map((result) => (result.isSuccess ? (result.value.find((form) => form.uid === formUid) ?? null) : null)),
      shareReplay(1),
    );
    this.formCache.set(formUid, request);
    return request;
  }

  private routeByForm<T>(formUid: string, onForm: (form: BookingFormDetails) => ObservableResult<T>): ObservableResult<T> {
    return this.findForm(formUid).pipe(switchMap((form) => (form ? onForm(form) : this.fail('getAppointment')))) as ObservableResult<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private fail(errorKey: string): ObservableResult<any> {
    return ObservableResult.ofError(this.translocoService.translate(`translations.bookings.errors.${errorKey}`));
  }

  private withHost<T>(
    resourceUid: string,
    next: (form: BookingFormDetails, host: BookingHost) => ObservableResult<T>,
  ): ObservableResult<T> {
    const resource = decodeResourceId(resourceUid);
    if (!resource) {
      return this.fail('getResources');
    }
    return this.findForm(resource.formUid).pipe(
      switchMap((form) => {
        const host = form?.hosts.find((candidate) => candidate.userUid === resource.hostUserUid);
        return host
          ? next(form, host)
          : ObservableResult.ofError(this.translocoService.translate('translations.bookings.errors.getResources'));
      }),
    ) as ObservableResult<T>;
  }

  private updateForm(
    form: BookingFormDetails,
    change: (request: BookingFormUpsertRequest) => BookingFormUpsertRequest,
  ): ObservableResult<BookingFormDetails> {
    return defer(() => {
      const previous = this.pendingWrites.get(form.uid) ?? Promise.resolve();
      const write = previous.catch(() => undefined).then(() => firstValueFrom(this.writeToLatest(form.uid, change)));
      this.pendingWrites.set(form.uid, write);
      return fromPromise(write);
    }) as ObservableResult<BookingFormDetails>;
  }

  private writeToLatest(
    formUid: string,
    change: (request: BookingFormUpsertRequest) => BookingFormUpsertRequest,
  ): ObservableResult<BookingFormDetails> {
    return this.bookingForms.getFormsByUids([formUid]).pipe(
      switchMap((result) => {
        const latest = result.isSuccess ? result.value.find((candidate) => candidate.uid === formUid) : null;
        return latest ? this.bookingForms.updateForm(formUid, change(toUpsertRequest(latest))) : this.fail('getAppointment');
      }),
      tap(() => this.formCache.delete(formUid)),
      map((result: Result<BookingFormDetails>) => result),
    ) as ObservableResult<BookingFormDetails>;
  }
}
